import { type } from 'arktype';
import { planVersionDataSchema } from './plan-version-data.model';
import type { PlanVersionSummary } from './plan-version-summary.model';

export const PlanVersionStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;
export type PlanVersionStatus = (typeof PlanVersionStatus)[keyof typeof PlanVersionStatus];

export const planVersionQuerySchema = type({
  'planId?': 'string',
  'versionNumber?': 'number',
  'status?': 'string',
});

export type PlanVersionQuery = typeof planVersionQuerySchema.t;

/**
 * Model for creating a new plan version.
 */
export const createPlanVersionSchema = type({
  planId: type('string').configure({
    description: 'ID of the plan this version belongs to.',
    examples: ['plan_abc123'],
  }),
  versionNumber: type('number').configure({
    description: 'Monotonically increasing version number within the plan, starting at 1.',
    examples: [1, 2],
  }),
  'status?': type('string').configure({
    description: "Lifecycle status of the version. One of 'draft' (editable), 'published' (active), 'archived' (retained for history).",
    examples: ['draft', 'published'],
  }),
  data: planVersionDataSchema.configure({
    description: 'Weekday-keyed assignment map (employeeId → shiftUnitId[]) plus optional multi-week and `__meta` entries.',
  }),
  'createdBy?': type('string').configure({
    description: 'Optional ID of the user who authored this version. Typically derived from the authenticated user on the server.',
    examples: ['user_abc123'],
  }),
}).configure({ description: 'Payload for creating a new plan version (snapshot of assignments + metadata).' });

export type CreatePlanVersion = typeof createPlanVersionSchema.t;

/**
 * Model for updating an existing plan version.
 */
export const updatePlanVersionSchema = type({
  'versionNumber?': type('number').configure({
    description: 'Updated version number. Rarely set directly — typically managed by the server.',
    examples: [2],
  }),
  'status?': type('string').configure({
    description: "Updated lifecycle status. One of 'draft', 'published', 'archived'.",
    examples: ['published'],
  }),
  'name?': type('string <= 100 | null').configure({
    description:
      'Human-readable label for this version. Max 100 chars. Pass `null` (or omit) to clear the label and fall back to the `vN` display.',
    examples: ['Pre-summer holiday draft', 'Customer-approved'],
  }),
  'data?': planVersionDataSchema.configure({
    description: 'Replacement assignment data for the version. Overwrites the existing data.',
  }),
}).configure({ description: 'Partial update of a plan version — fields omitted from the body remain unchanged.' });

export type UpdatePlanVersion = typeof updatePlanVersionSchema.t;

/**
 * Response type for PlanVersion entity from API.
 * Includes all database fields plus computed summary.
 */
export interface PlanVersionResponse {
  id: string;
  planId: string;
  versionNumber: number;
  name: string | null;
  status: string;
  data?: Record<string, unknown> | null;
  summary?: PlanVersionSummary | null;
  createdBy?: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}
